import { createApi } from '@reduxjs/toolkit/query/react'
import type { PssJournalCoverage, PssMirror, PssMirrorRun } from '@atbash-labs/ontology-ingest'

import { ontologyBaseQuery } from '../core'

interface PssMirrorsEnvelope {
  mirrors?: PssMirror[]
}

interface PssMirrorEnvelope {
  mirror: PssMirror
}

interface PssMirrorRunsEnvelope {
  runs?: PssMirrorRun[]
}

interface PssJournalCoverageEnvelope {
  coverage?: PssJournalCoverage
}

export interface ListPssMirrorRunsInput {
  mirrorId: string
  limit?: number
}

export interface UpdatePssMirrorRefreshInput {
  mirrorId: string
  refreshIntervalSeconds: number
}

export const pssMirrorsApi = createApi({
  reducerPath: 'pssMirrorsApi',
  baseQuery: ontologyBaseQuery,
  tagTypes: ['PssMirror', 'PssMirrorRuns'],
  endpoints: (builder) => ({
    listPssMirrors: builder.query<PssMirror[], void>({
      query: () => '/pss/mirrors',
      transformResponse: (response: PssMirrorsEnvelope) => response.mirrors ?? [],
      providesTags: (mirrors) => [
        { type: 'PssMirror', id: 'LIST' },
        ...(mirrors ?? []).map((mirror) => ({
          type: 'PssMirror' as const,
          id: mirror.id
        }))
      ]
    }),
    listPssMirrorRuns: builder.query<PssMirrorRun[], ListPssMirrorRunsInput>({
      query: ({ mirrorId, limit = 50 }) => ({
        url: `/pss/mirrors/${encodeURIComponent(mirrorId)}/runs`,
        params: { limit }
      }),
      transformResponse: (response: PssMirrorRunsEnvelope) => response.runs ?? [],
      providesTags: (_runs, _error, { mirrorId }) => [{ type: 'PssMirrorRuns', id: mirrorId }]
    }),
    getPssJournalCoverage: builder.query<PssJournalCoverage, { mirrorId: string }>({
      query: ({ mirrorId }) => `/pss/mirrors/${encodeURIComponent(mirrorId)}/journal-coverage`,
      transformResponse: (response: PssJournalCoverageEnvelope) => {
        if (!response.coverage) throw new Error('Missing journal coverage')
        return response.coverage
      },
      providesTags: (_coverage, _error, { mirrorId }) => [{ type: 'PssMirror', id: mirrorId }]
    }),
    updatePssMirrorRefresh: builder.mutation<PssMirror, UpdatePssMirrorRefreshInput>({
      query: ({ mirrorId, refreshIntervalSeconds }) => ({
        url: `/pss/mirrors/${encodeURIComponent(mirrorId)}`,
        method: 'PATCH',
        body: { refresh_interval_seconds: refreshIntervalSeconds }
      }),
      transformResponse: (response: PssMirrorEnvelope) => response.mirror,
      invalidatesTags: (_mirror, _error, { mirrorId }) => [
        { type: 'PssMirror', id: mirrorId },
        { type: 'PssMirror', id: 'LIST' }
      ]
    })
  })
})

export const {
  useListPssMirrorsQuery,
  useListPssMirrorRunsQuery,
  useGetPssJournalCoverageQuery,
  useUpdatePssMirrorRefreshMutation
} = pssMirrorsApi
